import React from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    createLocalEntity,
    mapLocalEntityToCardPayload,
} from '../../../firebase/firestore/chartLocalEntities';
import {uploadEntityAvatar} from '../../../firebase/firestore/entityAvatar';
import {getEntityTypeLabel} from '../../../utils/entityTypeMeta';

export const CUSTOM_ENTITY_TYPES = [
    'PERSON',
    'ORG',
    'LOC',
    'EVENT',
    'PRODUCT',
    'NORP',
    'UNKNOWN',
];

function parseAliases(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return [...new Set(
        raw
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean),
    )];
}

/**
 * Dialog to mint a custom (D…) entity in the knowledge base.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(entity: Object) => void} [props.onSuccess]
 * @param {string} [props.initialName] Pre-filled name when the dialog opens (e.g. chart search query).
 */
export default function CreateCustomEntityDialog({open, onClose, onSuccess, initialName = ''}) {
    const [canonicalName, setCanonicalName] = React.useState('');
    const [entityType, setEntityType] = React.useState('PERSON');
    const [description, setDescription] = React.useState('');
    const [aliasesRaw, setAliasesRaw] = React.useState('');
    const [imageFile, setImageFile] = React.useState(null);
    const [touched, setTouched] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) return;
        setCanonicalName(typeof initialName === 'string' ? initialName.trim() : '');
        setEntityType('PERSON');
        setDescription('');
        setAliasesRaw('');
        setImageFile(null);
        setTouched(false);
        setSaving(false);
        setError(null);
    }, [open, initialName]);

    const nameError = touched && !canonicalName.trim() ? 'Name is required' : '';

    const handleClose = () => {
        if (saving) return;
        onClose?.();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setTouched(true);
        const trimmedName = canonicalName.trim();
        if (!trimmedName) return;

        setSaving(true);
        setError(null);
        try {
            const response = await createLocalEntity({
                canonicalName: trimmedName,
                entityType,
                description: description.trim() || undefined,
                aliases: parseAliases(aliasesRaw),
            });
            let entity = mapLocalEntityToCardPayload(response);
            if (imageFile) {
                const {imageUrl} = await uploadEntityAvatar(entity.id, imageFile);
                entity = {...entity, imageUrl};
            }
            onSuccess?.(entity);
            onClose?.();
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Create custom entity</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{pt: 1}}>
                        {error ? (
                            <Alert severity="error">
                                {error.message || 'Failed to create custom entity.'}
                            </Alert>
                        ) : null}

                        <TextField
                            autoFocus
                            required
                            label="Name"
                            value={canonicalName}
                            onChange={(event) => setCanonicalName(event.target.value)}
                            disabled={saving}
                            error={Boolean(nameError)}
                            helperText={nameError || 'Canonical display name in the knowledge base'}
                            fullWidth
                        />

                        <FormControl fullWidth disabled={saving}>
                            <InputLabel id="custom-entity-type-label">Type</InputLabel>
                            <Select
                                labelId="custom-entity-type-label"
                                label="Type"
                                value={entityType}
                                onChange={(event) => setEntityType(event.target.value)}
                            >
                                {CUSTOM_ENTITY_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {getEntityTypeLabel(type) || type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Description"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            disabled={saving}
                            multiline
                            minRows={2}
                            fullWidth
                        />

                        <TextField
                            label="Aliases"
                            value={aliasesRaw}
                            onChange={(event) => setAliasesRaw(event.target.value)}
                            disabled={saving}
                            helperText="Comma-separated alternate names"
                            fullWidth
                        />

                        <Stack spacing={0.5}>
                            <Button
                                component="label"
                                variant="outlined"
                                disabled={saving}
                                sx={{alignSelf: 'flex-start'}}
                            >
                                {imageFile ? 'Change image' : 'Add image (optional)'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(event) => {
                                        setImageFile(event.target.files?.[0] || null);
                                    }}
                                />
                            </Button>
                            {imageFile ? (
                                <Typography variant="caption" color="text.secondary">
                                    {imageFile.name}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={saving}>
                        {saving ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
